/**
 * Which scrapers can run for a given brand, and what the Run modal may offer.
 *
 * Pure so it can be tested without a modal: the gating question ("does this
 * brand get a Run scraper button at all?") is the one thing here that an
 * operator notices being wrong, and it depends on a descriptor field that comes
 * off a service on someone's laptop.
 */

/** `GET /scrapers` — a module descriptor plus the module version. */
export interface ScraperDescriptor {
  id: string;
  displayName: string;
  /** brandIds this module can scrape, or 'all' for a multi-brand source. */
  supportedBrands: readonly string[] | 'all';
  description: string;
  /** Absent means `['full']` and nothing else. */
  supportedModes?: readonly string[];
  version: string;
}

/** The brand fields the matcher needs — a subset of `WatchBrand`. */
export interface BrandRef {
  id: string;
  name?: string;
}

/**
 * `descriptorBrandId → watchBrands doc id`, from `admin_config/extractor_config`.
 *
 * The two sides derive their slugs independently — the extractor from the
 * module's own brand name, the admin from whatever created the brand document —
 * and when they disagree the Run button silently does not render, which reads as
 * "the feature is broken" rather than "these two strings differ by one
 * character". This map is the admin-editable reconciliation, so a divergence is
 * fixed from the config screen instead of by a release in the extractor repo.
 */
export type BrandOverrides = Readonly<Record<string, string>>;

/**
 * Lower-case and trim both halves, dropping anything that isn't a usable pair.
 *
 * Applied on the way in AND at every read, because the map is Firestore data
 * that an operator types: a stray capital in either half would otherwise be a
 * silent no-op, which is the exact failure this feature exists to end.
 */
export function normalizeBrandOverrides(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string') continue;
    const from = key.trim().toLowerCase();
    const to = value.trim().toLowerCase();
    if (from === '' || to === '') continue;
    out[from] = to;
  }
  return out;
}

/**
 * The watchBrands doc id a descriptor entry stands for.
 *
 * An override WINS over the literal id rather than being an alternative to it.
 * Both are ids in the same namespace, so "match either" would leave a descriptor
 * that an operator has deliberately redirected still answering to its old id —
 * and the redirect exists precisely because that old id is the wrong brand.
 */
export function resolveDescriptorBrandId(
  descriptorBrandId: string,
  overrides?: BrandOverrides | null,
): string {
  const id = descriptorBrandId.trim().toLowerCase();
  if (id === '') return '';
  const mapped = overrides?.[id];
  if (typeof mapped !== 'string') return id;
  const target = mapped.trim().toLowerCase();
  return target === '' ? id : target;
}

/**
 * The mode a descriptor that declares none is understood to run.
 *
 * shared-types makes this a convention rather than a required field, which is
 * what let `supportedModes` be added without touching the modules that predate
 * it. Omega leaves it unset because its source publishes no new-releases view.
 */
export const DEFAULT_MODES: readonly string[] = ['full'];

/**
 * Scrapers that can crawl this brand.
 *
 * Matched on `brand.id`, per the memo. A watchBrands doc id IS the brand slug
 * in this admin — there is no separate `slug` field on `WatchBrand` — and the
 * modules populate `supportedBrands` with exactly those ids
 * (`['christopher-ward']`, `['omega']`). Comparison is case-insensitive and
 * trimmed to survive a hand-edited descriptor.
 *
 * `overrides` is what reconciles the brand-slug divergences the memo lists in
 * §7. Each `supportedBrands` entry is resolved through the map before it is
 * compared, so a module whose descriptor says `glashtte-original` can be pointed
 * at whatever the brand document is actually keyed on without a change in either
 * repo. The map is normalized here rather than trusted, because callers hand it
 * straight out of Firestore.
 */
export function scrapersForBrand(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  brand: BrandRef | null | undefined,
  overrides?: BrandOverrides | null,
): ScraperDescriptor[] {
  if (!descriptors || !brand?.id) return [];
  const brandId = brand.id.trim().toLowerCase();
  if (brandId === '') return [];

  const map = normalizeBrandOverrides(overrides);
  return descriptors.filter((d) => {
    if (d.supportedBrands === 'all') return true;
    if (!Array.isArray(d.supportedBrands)) return false;
    return d.supportedBrands.some(
      (b) => typeof b === 'string' && resolveDescriptorBrandId(b, map) === brandId,
    );
  });
}

/**
 * Modes to offer for a descriptor.
 *
 * Never invent one. The extractor validates `mode` against this same list and
 * answers `400 unsupported_mode`, so sending something undeclared is a round
 * trip that can only fail — and before that validation existed it was worse
 * than a failure, because the mode was dropped and a full-catalogue crawl ran
 * in its place.
 */
export function modesForScraper(descriptor: ScraperDescriptor | null | undefined): string[] {
  const declared = descriptor?.supportedModes;
  if (!Array.isArray(declared) || declared.length === 0) return [...DEFAULT_MODES];
  return declared.filter((m): m is string => typeof m === 'string' && m !== '');
}

/**
 * Operator-facing copy for the modes the extractors currently declare.
 *
 * A lookup rather than a field on the descriptor, because the mode strings are
 * a small closed vocabulary shared across every module (`ExtractorMode` in
 * shared-types) — the wording belongs with the admin that shows it, not
 * duplicated into each module's descriptor.
 *
 * Deliberately partial. A module can declare a mode this admin has never heard
 * of — that is how `heritage` arrived, ahead of this map — and the picker must
 * keep offering it, so both accessors below fall back to the raw value rather
 * than hiding or renaming an unknown mode. Adding an entry here is a copy
 * change, never a gate.
 */
const MODE_LABELS: Readonly<Record<string, string>> = {
  full: 'Full catalogue',
  'new-only': 'New releases only',
  heritage: 'Heritage / Vintage',
};

const MODE_DESCRIPTIONS: Readonly<Record<string, string>> = {
  full: 'Every model the source lists.',
  'new-only': 'Just what the source shows on its new-releases view.',
  heritage: 'Discontinued and historical models — the Heritage/pre-owned boutique.',
};

/** The mode's operator-facing name, or the raw value for one we do not know. */
export function modeLabel(mode: string): string {
  return MODE_LABELS[mode] ?? mode;
}

/** One line on what the mode crawls, or nothing for a mode we do not know. */
export function modeDescription(mode: string): string | undefined {
  return MODE_DESCRIPTIONS[mode];
}

/**
 * A picker is only worth rendering when there is a choice — one mode is a fact
 * about the module, not a decision for the operator.
 */
export function shouldShowModePicker(descriptor: ScraperDescriptor | null | undefined): boolean {
  return modesForScraper(descriptor).length > 1;
}

/**
 * The gate on the brand page: the flag, then a scraper that covers this brand.
 *
 * Both halves are load-bearing. The flag is the rollout lever (the URL points
 * at a laptop that is not always on, and an intermittently-failing button is
 * worse than no button); the descriptor match is what keeps the button off the
 * 400-odd brands nothing can crawl.
 */
export function canRunScraper(
  featureEnabled: boolean,
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  brand: BrandRef | null | undefined,
  overrides?: BrandOverrides | null,
): boolean {
  return featureEnabled && scrapersForBrand(descriptors, brand, overrides).length > 0;
}

/**
 * `brandId → module display names`, for every brand a module explicitly names.
 *
 * Built once per listing rather than calling `scrapersForBrand` per row: the
 * brands screen renders hundreds of rows against the whole catalogue, and the
 * per-row question ("is there a scraper for this brand?") is a map lookup once
 * the descriptors have been resolved through the overrides a single time.
 *
 * `supportedBrands: 'all'` is deliberately NOT expanded here, which is the one
 * place this diverges from `scrapersForBrand`. A multi-brand module would put
 * the badge on every row at once, and a badge every row carries says nothing —
 * so the listing answers the narrower question "a brand-specific module exists"
 * while the Run button keeps answering "something can crawl this brand". No
 * module declares `'all'` today; the divergence is a decision taken in advance
 * rather than a behaviour anyone currently sees.
 *
 * Display names are collected because a brand can be covered by more than one
 * module (a catalogue crawler and a heritage crawler, say) and naming them is
 * the difference between a badge that says a scraper exists and one that says
 * WHICH. Duplicates are dropped; a module with no usable name is skipped from
 * the list but still counts as coverage.
 */
export function scraperBrandDisplayNames(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  overrides?: BrandOverrides | null,
): Map<string, string[]> {
  const byBrand = new Map<string, string[]>();
  if (!descriptors) return byBrand;

  const map = normalizeBrandOverrides(overrides);
  for (const descriptor of descriptors) {
    if (!Array.isArray(descriptor?.supportedBrands)) continue;
    const name = typeof descriptor.displayName === 'string' ? descriptor.displayName.trim() : '';
    for (const entry of descriptor.supportedBrands) {
      if (typeof entry !== 'string') continue;
      const brandId = resolveDescriptorBrandId(entry, map);
      if (brandId === '') continue;
      const names = byBrand.get(brandId) ?? [];
      if (name !== '' && !names.includes(name)) names.push(name);
      byBrand.set(brandId, names);
    }
  }
  return byBrand;
}
